import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useField} from 'formik'
import {TComponentTextareaOptionsProps} from './_types'
import ComponentButton from '../ComponentButton'
import {useIntl} from 'react-intl'
import ComponentCheckbox from '../ComponentCheckbox'
import {useTextareaOptions} from './hook/useTextareaOptions'

type TEnterMode = 'enter' | 'ctrlEnter'

const STORAGE_KEY = 'textareaEnterMode'

const getEnterModeLabel = (mode: TEnterMode) => {
    if (mode === 'ctrlEnter') return 'Ctrl + Enter'
    return 'Enter'
}

const ComponentTextareaOptions: React.FC<TComponentTextareaOptionsProps> = (props) => {
    const {
        article,
        is_disabled,
        is_visible = true,
        settings = {},
        reloadKey,
        title,
        description,
    } = props
    const [field, meta] = useField<string>(article)
    const value = field.value
    const isError = Boolean(meta.error && meta.touched)
    const intl = useIntl()

    const logic = useTextareaOptions({
        article,
        settings,
        is_visible,
        reloadKey,
    })

    // Enter mode
    const [enterMode, setEnterMode] = useState<TEnterMode>('enter')
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isShortcutDropdownOpen, setIsShortcutDropdownOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY) as TEnterMode | null
            if (stored === 'enter' || stored === 'ctrlEnter') {
                setEnterMode(stored)
            }
        } catch {
            // ignore
        }
    }, [])

    const toggleMenu = useCallback(() => {
        setIsMenuOpen((prev) => !prev)
        setIsShortcutDropdownOpen(false)
    }, [])

    const selectMode = useCallback((mode: TEnterMode) => {
        setEnterMode(mode)
        setIsShortcutDropdownOpen(false)
        setIsMenuOpen(false)
        try {
            window.localStorage.setItem(STORAGE_KEY, mode)
        } catch {
            // ignore
        }
    }, [])

    useEffect(() => {
        if (!isMenuOpen && !isShortcutDropdownOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node | null
            if (!target || !menuRef.current) return
            if (menuRef.current.contains(target)) return
            setIsMenuOpen(false)
            setIsShortcutDropdownOpen(false)
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMenuOpen(false)
                setIsShortcutDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isMenuOpen, isShortcutDropdownOpen])

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (enterMode === 'enter') {
                if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    logic.handleSubmit()
                }
            } else {
                if (event.key === 'Enter' && event.ctrlKey) {
                    event.preventDefault()
                    logic.handleSubmit()
                }
            }
        },
        [enterMode, logic.handleSubmit]
    )

    const openDrawerAnnounced = React.useCallback(() => {
        window.dispatchEvent(new CustomEvent('textareaOptions:open', {detail: {article}}))
        logic.openDrawer()
    }, [article, logic])

    // слушаем чужие открытия, чтобы закрыться
    useEffect(() => {
        const onSomeoneOpened = (ev: Event) => {
            const detail = (ev as CustomEvent).detail as {article?: string}
            if (detail?.article !== article) logic.setOpen(false)
        }
        window.addEventListener('textareaOptions:open', onSomeoneOpened as EventListener)
        return () =>
            window.removeEventListener('textareaOptions:open', onSomeoneOpened as EventListener)
    }, [article, logic])

    if (!is_visible) return null

    const resClass = `componentTextarea form-control form-control-solid${isError ? ' invalid' : ''}`

    return (
        <>
            <div
                ref={logic.rootRef}
                className={`componentTextareaOptions ${
                    logic.open ? 'componentTextareaOptions--open' : ''
                }`}
            >
                {/* ROW */}
                <div className='componentTextareaOptions__row'>
                    <div ref={menuRef} style={{position: 'relative', flex: 1}}>
                        <div className='componentTextarea_wrapper'>
                            <textarea
                                ref={logic.taRef}
                                name={article}
                                className={resClass}
                                value={value}
                                rows={logic.rows}
                                placeholder={logic.placeholder ?? description ?? 'Введите текст…'}
                                onFocus={openDrawerAnnounced}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={is_disabled}
                                onKeyDown={handleKeyDown}
                            />
                            <button
                                type='button'
                                className='componentTextarea_cornerIndicator'
                                onClick={toggleMenu}
                                aria-label='Настройки ввода Enter'
                            >
                                <i
                                    className='bi bi-gear componentTextarea_cornerIconInner'
                                    aria-hidden='true'
                                />
                            </button>
                        </div>

                        {isMenuOpen && (
                            <div className='componentTextarea_menu'>
                                <div className='componentTextarea_menuHeader'>Горячие клавиши</div>
                                <div className='componentTextarea_menuRow'>
                                    <div className='componentTextarea_menuRowLabel'>
                                        Отправить сообщение
                                    </div>
                                    <div className='componentTextarea_menuSelectWrap'>
                                        <button
                                            type='button'
                                            className='componentTextarea_menuSelect'
                                            onClick={() =>
                                                setIsShortcutDropdownOpen((prev) => !prev)
                                            }
                                        >
                                            <span>{getEnterModeLabel(enterMode)}</span>
                                            <i className='bi bi-chevron-down' />
                                        </button>
                                        {isShortcutDropdownOpen && (
                                            <div className='componentTextarea_menuDropdown'>
                                                <button
                                                    type='button'
                                                    className={`componentTextarea_menuOption${enterMode === 'enter' ? ' active' : ''}`}
                                                    onClick={() => selectMode('enter')}
                                                >
                                                    <div className='componentTextarea_menuOptionText'>
                                                        <div className='componentTextarea_menuOptionTitle'>
                                                            Enter
                                                        </div>
                                                        <div className='componentTextarea_menuOptionHint'>
                                                            Shift + Enter — перенос строки
                                                        </div>
                                                    </div>
                                                    {enterMode === 'enter' && (
                                                        <i className='bi bi-check componentTextarea_menuOptionCheck' />
                                                    )}
                                                </button>
                                                <button
                                                    type='button'
                                                    className={`componentTextarea_menuOption${enterMode === 'ctrlEnter' ? ' active' : ''}`}
                                                    onClick={() => selectMode('ctrlEnter')}
                                                >
                                                    <div className='componentTextarea_menuOptionText'>
                                                        <div className='componentTextarea_menuOptionTitle'>
                                                            Ctrl + Enter
                                                        </div>
                                                        <div className='componentTextarea_menuOptionHint'>
                                                            Enter — перенос строки
                                                        </div>
                                                    </div>
                                                    {enterMode === 'ctrlEnter' && (
                                                        <i className='bi bi-check componentTextarea_menuOptionCheck' />
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='componentTextareaOptions__side'>
                        <ComponentButton
                            className='componentTextareaOptions__createButton'
                            type='custom'
                            settings={{
                                title: logic.creating
                                    ? intl.formatMessage({id: 'FIELD.SAVING'})
                                    : logic.created
                                    ? intl.formatMessage({id: 'FIELD.SAVED'})
                                    : intl.formatMessage({id: 'FIELD.CREATE_TEMPLATE'}),
                                background: 'primary-light',
                                icon: 'plus-circle',
                            }}
                            defaultLabel='icon'
                            loading={logic.creating}
                            disabled={logic.creating}
                            customHandler={() => logic.createTemplate(value)}
                        />

                        <div className='componentTextareaOptions__tools'>
                            <ComponentCheckbox
                                label={intl.formatMessage({id: 'FIELD.FOR_PRINT'})}
                                customChecked={logic.forPrint}
                                customHandler={() => logic.setForPrint((p) => !p)}
                                className='for-print-toggle'
                                buttonLike
                                hoverLabel
                            />
                        </div>
                    </div>
                </div>

                {/* ERROR */}
                {isError ? <div className='invalid_feedback'>{meta.error as string}</div> : null}
            </div>

            {/* OVERLAY */}
            <div
                className={`componentTextareaOptions__overlay ${
                    logic.open ? 'componentTextareaOptions__overlay--open' : ''
                }`}
                onClick={() => logic.setOpen(false)}
                style={{pointerEvents: 'none'}}
            />

            {/* DRAWER */}
            <aside
                ref={logic.drawerRef}
                className={`componentTextareaOptions__drawer ${
                    logic.open ? 'componentTextareaOptions__drawer--open' : ''
                }`}
                aria-hidden={!logic.open}
            >
                <div className='componentTextareaOptions__head'>
                    <input
                        className='componentTextareaOptions__search'
                        value={logic.search}
                        onChange={(e) => logic.setSearch(e.target.value)}
                        placeholder={intl.formatMessage({id: 'SEARCH.PLACEHOLDER'})}
                    />
                </div>

                <div className='componentTextareaOptions__list'>
                    {logic.isLoading && (
                        <div className='componentTextareaOptions__empty'>
                            <svg className='splash-spinner' viewBox='0 0 50 50'>
                                <circle
                                    className='path'
                                    cx='25'
                                    cy='25'
                                    r='20'
                                    fill='none'
                                    strokeWidth='5'
                                ></circle>
                            </svg>
                        </div>
                    )}
                    {!logic.isLoading && logic.filtered.length === 0 && (
                        <div className='componentTextareaOptions__empty'>
                            {intl.formatMessage({id: 'SEARCH.EMPTY_LIST'})}
                        </div>
                    )}
                    {!logic.isLoading &&
                        logic.filtered.map((o) => (
                            <button
                                key={String(o.value)}
                                type='button'
                                className='componentTextareaOptions__item'
                                onClick={() => logic.addText(o.label, value, article)}
                            >
                                {o.label}
                            </button>
                        ))}
                </div>
            </aside>
        </>
    )
}

export default ComponentTextareaOptions